import cn from "classnames"
import React, { useMemo, useRef, useState } from "react"
import { Input } from "reactstrap"
import { getDistinct, IconButton, sortAscending, SpinnerContainer, useSelector } from "swiipe.portal.shared"
import { translationActions, translationSelectors } from "../../../store/reducers/translationSlice"
import { addTranslationTagThunk, deleteTranslationTagThunk } from "../../../store/thunks/translationsThunks"
import useReduxDispatch from "../../../store/useReduxDispatch"
import "./TranslationTagField.scss"

interface ITranslationTagFieldProps {
    subKey?: string
    tagKey?: string
    group: string
    className?: string
}

const TranslationTagField = ({ subKey, tagKey, group, className }: ITranslationTagFieldProps) => {
    const dispatch = useReduxDispatch()
    const [showOverlay, setShowOverlay] = useState(false)
    const [activatedTags, setActivatedTags] = useState<string[]>([])
    const inputRef = useRef<HTMLInputElement>(null)
    const tags = useSelector(translationSelectors.tags)
    const lastAddedTagName = useSelector(translationSelectors.lastAddedTagName)
    const showAddTags = useSelector(translationSelectors.showAddTags)
    const showCalculatedTags = useSelector(translationSelectors.showCalculatedTags)
    const showLastAddedTagSuggestion = useSelector(translationSelectors.showLastAddedTagSuggestion)
    const tagNames = useMemo(
        () =>
            sortAscending(
                getDistinct(tags, (t) => t.name).map((t) => t.name),
                (name) => name
            ),
        [tags]
    )
    const [tagSearch, setTagSearch] = useState("")
    const [showAllTags, setShowAllTags] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [isQuickAdding, setIsQuickAdding] = useState(false)
    const [isDeletingList, setIsDeletingList] = useState<string[]>([])

    const tagsForSearch = useMemo(
        () =>
            !tagSearch
                ? []
                : sortAscending(
                      tagNames.filter((tn) => tn.includes(tagSearch)),
                      (tn) => tn.length
                  ),
        [tagSearch, tagNames]
    )

    const tagsForGroup = tags.filter((t) => t.group === group)
    const inheritedTags =
        !tagKey && !subKey
            ? []
            : tagsForGroup.filter(
                  (t) =>
                      !t.key &&
                      (!t.subKey ||
                          (subKey?.startsWith(t.subKey) && subKey[t.subKey.length] === ".") ||
                          (tagKey?.startsWith(t.subKey) && tagKey[t.subKey.length] === "."))
              )
    const relevantTags = tagsForGroup.filter((t) => (t.key ?? "") === (tagKey ?? "") && (t.subKey ?? "") === (subKey ?? ""))

    const addTag = async (name: string, isQuickAdding: boolean) => {
        if (isQuickAdding) {
            setIsQuickAdding(true)
        } else {
            setIsAdding(true)
        }
        await dispatch(
            addTranslationTagThunk({
                group: group,
                key: tagKey,
                name: name,
                subKey: subKey,
                tagId: "",
            })
        )
        if (isQuickAdding) {
            setIsQuickAdding(false)
        } else {
            setIsAdding(false)
        }
        setTagSearch("")
        setShowOverlay(false)
        dispatch(translationActions.setLastAddedTagName(name))
    }

    return (
        <div className={cn("translation-tag-field", showAddTags && "translation-tag-field--show-add", className)}>
            <div className={cn("translation-tag-field__initial")}>
                {showCalculatedTags && tagKey && inheritedTags.length + relevantTags.length > 0 && (
                    <div className="translation-tag-field__tag translation-tag-field__tag-total">
                        {[...inheritedTags, ...relevantTags].map((tag) => (
                            <div key={tag.tagId}>{tag.name}</div>
                        ))}
                    </div>
                )}

                {showAddTags && (
                    <div className="translation-tag-field__tag-add">
                        <span>&nbsp;</span> {/* Needed for propert layout */}
                        <IconButton
                            icon="add"
                            text=""
                            onClick={() => {
                                setShowOverlay(!showOverlay)
                                setTimeout(() => {
                                    inputRef.current?.focus()
                                }, 50)
                            }}
                        />
                    </div>
                )}
                {showAddTags &&
                    showLastAddedTagSuggestion &&
                    lastAddedTagName &&
                    !relevantTags.some((t) => t.name === lastAddedTagName) && (
                        <div
                            className="translation-tag-field__tag translation-tag-field__tag-quick"
                            onClick={async () => {
                                addTag(lastAddedTagName, true)
                            }}
                        >
                            <SpinnerContainer showSpinner={isQuickAdding} wrapperClassName="translation-tag-field__tag-inner">
                                <span>{lastAddedTagName}</span>
                                <IconButton icon="add" className="translation-tag-field__tag-quick-add" text="" isXSmall />
                                <span>?</span>
                            </SpinnerContainer>
                        </div>
                    )}
                {relevantTags.map((t) => {
                    const isActivated = activatedTags.includes(t.name)
                    const isDeleting = isDeletingList.includes(t.name)
                    return (
                        <div className="translation-tag-field__tag" key={t.tagId}>
                            <SpinnerContainer showSpinner={isDeleting} wrapperClassName="translation-tag-field__tag-inner">
                                <span
                                    onClick={() => {
                                        if (isActivated) {
                                            setActivatedTags(activatedTags.filter((at) => at !== t.name))
                                        } else {
                                            setActivatedTags([...activatedTags, t.name])
                                        }
                                    }}
                                >
                                    {t.name}
                                </span>
                                {isActivated && (
                                    <IconButton
                                        icon="close"
                                        className="translation-tag-field__tag-delete"
                                        text=""
                                        isXSmall
                                        onClick={async () => {
                                            setIsDeletingList([...isDeletingList, t.name])
                                            await dispatch(deleteTranslationTagThunk(t))
                                            setIsDeletingList(isDeletingList.filter((n) => n !== t.name))
                                        }}
                                    />
                                )}
                            </SpinnerContainer>
                        </div>
                    )
                })}
            </div>
            {showAddTags && showOverlay && (
                <div className={cn("translation-tag-field__overlay")}>
                    <SpinnerContainer showSpinner={isAdding}>
                        <Input
                            innerRef={inputRef}
                            className={cn("translation-tag-field__input")}
                            onChange={(e) => {
                                setTagSearch(e.currentTarget.value.toLowerCase())
                            }}
                            onKeyUp={async (e) => {
                                if (e.key === "Enter" || e.keyCode === 13) {
                                    await addTag(tagSearch, false)
                                }
                                if (e.key === "Escape") {
                                    setShowOverlay(false)
                                }
                            }}
                            value={tagSearch}
                            type="text"
                        ></Input>
                    </SpinnerContainer>
                    {!isAdding && (
                        <div className="translation-tag-field__overlay-results">
                            {
                                <div
                                    className={cn(
                                        "translation-tag-field__overlay-results-item translation-tag-field__overlay-results-item-all-tags",
                                        !tagSearch &&
                                            !showAllTags &&
                                            "translation-tag-field__overlay-results-item-all-tags-no-bottom-border"
                                    )}
                                    onClick={() => setShowAllTags(!showAllTags)}
                                >
                                    {showAllTags ? "Hide full tag list" : "Show full tag list"}
                                </div>
                            }
                            {!showAllTags && tagSearch && !tagsForSearch.includes(tagSearch) && (
                                <div
                                    className="translation-tag-field__overlay-results-item"
                                    onClick={() => addTag(tagSearch, false)}
                                >
                                    {tagSearch} (new)
                                </div>
                            )}
                            {(showAllTags ? tagNames : tagsForSearch).map((tagName) => {
                                return (
                                    <div
                                        key={tagName}
                                        className="translation-tag-field__overlay-results-item"
                                        onClick={() => addTag(tagName, false)}
                                    >
                                        {tagName}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default React.memo(TranslationTagField)
